import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { JobCatalogTreeFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'

import { useFixFlowProvider } from 'modules/contracts/components/FixFlow/FixFlowProvider'
import Dates from 'modules/contracts/components/FlowPage/Steps/Dates'
import LegalAndExtras from 'modules/contracts/components/FlowPage/Steps/LegalAndExtras'
import RoleLevelSalary from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary'
import WorkHours from 'modules/contracts/components/FlowPage/Steps/WorkHours'
import ContractFlow from 'modules/contracts/lib/ContractFlow'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import { FlowPageContent } from '../FlowPage/_FlowPageContent'
import Footer from './_Footer'
import DatesStep from './lib/DatesStep'
import LegalAndExtrasStep from './lib/LegalAndExtrasStep'
import RoleLevelSalaryStep from './lib/RoleLevelSalaryStep'
import WorkHoursStep from './lib/WorkHoursStep'
import { translations as datesStepTranslations } from './lib/datesTranslations'
import { translations as legalAndExtrasTranslations } from './lib/legalAndExtrasTranslations'
import { translations as roleLevelSalaryTranslations } from './lib/roleLevelSalaryTranslations'
import { translations as templateTranslations } from './lib/templateTranslations'
import { translations as workingHoursTranslations } from './lib/workingHoursTranslations'

interface Props {
  jobCatalogTrees?: JobCatalogTreeFragment[]
  jobCatalogTreeFeatureEnabled?: boolean
}

const FixFlowContent = ({ jobCatalogTrees, jobCatalogTreeFeatureEnabled }: Props) => {
  const { onSubmit: onFixContract, form, isSubmitting } = useFixFlowProvider()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { employee, contractVersion, legalEntityContractData } = useContractFlow()
  const canSeeWorkingHours: boolean = policyAuthorizations.for(
    'contracts.see_working_hours',
    employee
  )

  const onClose = () => {
    navigate(
      navigation.employees.id.contractVersions.contractVersionId.fullPath({
        id: employee.id.toString(),
        contractVersionId: contractVersion!.id.toString(),
      })
    )
  }

  const [isCurrentStepCompleted, setIsCurrentStepCompleted] = React.useState(false)

  const flowSteps = [
    new DatesStep(
      (
        <Dates
          step={1}
          type="fix"
          form={form}
          translations={datesStepTranslations()}
          setIsCurrentStepCompleted={setIsCurrentStepCompleted}
          contractVersion={contractVersion!}
        />
      )
    ),
    new RoleLevelSalaryStep(
      (
        <RoleLevelSalary
          step={2}
          type="fix"
          form={form}
          translations={roleLevelSalaryTranslations()}
          setIsCurrentStepCompleted={setIsCurrentStepCompleted}
          jobCatalogTrees={jobCatalogTrees}
          jobCatalogTreeFeatureEnabled={jobCatalogTreeFeatureEnabled}
        />
      )
    ),
  ]

  // Avoid showing a blank step when hours permissions are disabled
  if (canSeeWorkingHours) {
    flowSteps.push(
      new WorkHoursStep(
        (
          <WorkHours
            step={3}
            type="fix"
            form={form}
            translations={workingHoursTranslations()}
            setIsCurrentStepCompleted={setIsCurrentStepCompleted}
            legalEntity={employeeLegalEntity(employee)}
          />
        )
      )
    )
  }
  flowSteps.push(
    new LegalAndExtrasStep(
      (
        <LegalAndExtras
          step={canSeeWorkingHours ? 4 : 3}
          type="fix"
          form={form}
          translations={legalAndExtrasTranslations()}
          setIsCurrentStepCompleted={setIsCurrentStepCompleted}
        />
      )
    )
  )
  const fixContractFlow = useLazyRef(() => new ContractFlow(flowSteps)).current

  const onSubmit = () => {
    onFixContract(() => {
      navigate(
        navigation.employees.id.contractVersions.contractVersionId.fullPath({
          id: employee.id.toString(),
          contractVersionId: contractVersion!.id.toString(),
        })
      )
    })
  }

  return (
    <FlowPageContent
      translations={templateTranslations()}
      form={form}
      employee={employee!}
      contractVersion={contractVersion}
      legalEntityContractData={legalEntityContractData}
      title={i18n.t('contract_versions.flows.fix.header.title', {
        employeeFullName: employeeUser(employee!).fullName(),
      })}
      flow={fixContractFlow}
      footerContent={<Footer flow={fixContractFlow} employee={employee!} />}
      isCurrentStepCompleted={isCurrentStepCompleted}
      isSubmitting={isSubmitting}
      onClose={onClose}
      onSubmit={() => onSubmit()}
    />
  )
}

export default observer(FixFlowContent)
